import * as recipeCatalogJson from './media-filter-recipes.json';

export type MediaFilterPresetId =
  | 'original'
  | 'clarendon'
  | 'gingham'
  | 'moon'
  | 'lark'
  | 'reyes'
  | 'juno'
  | 'rio_de_janeiro'
  | 'los_angeles';

export type MediaFilterBlendMode =
  | 'source-over'
  | 'screen'
  | 'multiply'
  | 'overlay'
  | 'soft-light';

export interface MediaFilterLutAssetDefinition {
  brightness: number;
  contrast: number;
  saturation: number;
  grayscale: number;
  sepia: number;
  lift: [number, number, number];
  gamma: [number, number, number];
  tint: string;
  tintAmount: number;
  channelMix: [
    [number, number, number],
    [number, number, number],
    [number, number, number],
  ];
}

export interface MediaFilterTextureSpotDefinition {
  x: number;
  y: number;
  radius: number;
  color: string;
  opacity: number;
  softness: number;
}

export interface MediaFilterTextureBandDefinition {
  x1: number;
  y1: number;
  x2: number;
  y2: number;
  width: number;
  color: string;
  opacity: number;
  softness: number;
}

export interface MediaFilterOverlayAssetDefinition {
  baseColor: string;
  spots: ReadonlyArray<MediaFilterTextureSpotDefinition>;
  bands: ReadonlyArray<MediaFilterTextureBandDefinition>;
}

export interface MediaFilterGrainAssetDefinition {
  seed: number;
  cellSize: number;
  intensity: number;
}

export interface MediaFilterPresetDefinition {
  id: MediaFilterPresetId;
  labelKey: string;
  lutAsset: string;
  overlayAsset?: string | null;
  overlayBlendMode?: MediaFilterBlendMode | null;
  overlayOpacity?: number | null;
  grainAsset?: string | null;
  grainBlendMode?: MediaFilterBlendMode | null;
  grainOpacity?: number | null;
  grainScale?: number | null;
}

interface MediaFilterRecipeCatalogDefinition {
  defaultLutSize: number;
  presets: MediaFilterPresetDefinition[];
  lutAssets: Record<string, MediaFilterLutAssetDefinition>;
  overlayAssets: Record<string, MediaFilterOverlayAssetDefinition>;
  grainAssets: Record<string, MediaFilterGrainAssetDefinition>;
}

export const DEFAULT_MEDIA_FILTER_PRESET_ID: MediaFilterPresetId = 'original';

const recipeCatalog = recipeCatalogJson as unknown as MediaFilterRecipeCatalogDefinition;

export const MEDIA_FILTER_DEFAULT_LUT_SIZE = recipeCatalog.defaultLutSize;
export const MEDIA_FILTER_PRESETS: MediaFilterPresetDefinition[] = [...recipeCatalog.presets];

export function normalizeMediaFilterPresetId(value?: string | null): MediaFilterPresetId {
  const normalized = `${value || ''}`.trim().toLowerCase() as MediaFilterPresetId;
  return MEDIA_FILTER_PRESETS.some((preset) => preset.id === normalized)
    ? normalized
    : DEFAULT_MEDIA_FILTER_PRESET_ID;
}

export function getMediaFilterPreset(value?: string | null): MediaFilterPresetDefinition {
  const presetId = normalizeMediaFilterPresetId(value);
  return MEDIA_FILTER_PRESETS.find((preset) => preset.id === presetId) || MEDIA_FILTER_PRESETS[0];
}

export function getMediaFilterLutAsset(assetId: string): MediaFilterLutAssetDefinition {
  return recipeCatalog.lutAssets[assetId] || recipeCatalog.lutAssets['identity'];
}

export function getMediaFilterOverlayAsset(assetId?: string | null): MediaFilterOverlayAssetDefinition | null {
  if (!assetId) {
    return null;
  }

  return recipeCatalog.overlayAssets[assetId] || null;
}

export function getMediaFilterGrainAsset(assetId?: string | null): MediaFilterGrainAssetDefinition | null {
  if (!assetId) {
    return null;
  }

  return recipeCatalog.grainAssets[assetId] || null;
}

export function getMediaFilterPresetLutAsset(value?: string | null): MediaFilterLutAssetDefinition {
  return getMediaFilterLutAsset(getMediaFilterPreset(value).lutAsset);
}

export function hasMediaFilterRecipeEffects(value?: string | null): boolean {
  return normalizeMediaFilterPresetId(value) !== DEFAULT_MEDIA_FILTER_PRESET_ID;
}
